import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { once } from 'ramda';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    aiImprovePaymentTitle,
    aiImproveTariffsTitle,
    enableAiImprovePromoPopupAfterImprove,
    fileTypesForAiPhotoImproveMaxSize,
    isAiPhotoImproveEnabled,
} from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoImprove';
import {
    aiRmvBgrndNoLimit,
    enableAiRmvPromoPopupAfterRmv,
    isAiRemoveBackgroundEnabled,
} from 'reactApp/appHelpers/featuresHelpers/features/imageTools';
import { aiToolsAttemptsInit, improveImageRequest } from 'reactApp/modules/aiTools/aiTools.module';
import {
    getAiImageImproveAvailableAttempts,
    getAiProcessType,
    getAiRmvBgrndAvailableAttempts,
    getAiToolsEndTime,
    getAiToolsRmvBgrndEndTime,
    getImageImprovingState,
    getIsNewbackground,
} from 'reactApp/modules/aiTools/aiTools.selectors';
import type { ProcessingType } from 'reactApp/modules/aiTools/aiTools.types';
import { name } from 'reactApp/modules/aiTools/helpers/constants';
import { isPaymiumSelector } from 'reactApp/modules/aiTools/isPaymiumSelector';
import { sendDwhRadar } from 'reactApp/modules/dwh/dwh.helpers';
import { openThreeTariffsPopup } from 'reactApp/modules/payment/payment.helpers';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import type { CloudFile, EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { getDataFromStorage, saveDataToStorage } from 'reactApp/storage';
import type { RootState } from 'reactApp/store';
import { renderAfterImprovePromo, renderAfterRmvPromo, renderAiLimitPromo } from 'reactApp/ui/ReactViewer/AiImprove/AiImprovePopups';

const paySource = 'mnz_ai_improve';

const promoPopupId = 'ai_improve_ppp_after_improve';
const promoPopupIdAfterRmv = 'ai_improve_ppp_after_rmv_';
const MaxShowPromoPopupCount = 3;

interface PopupShowData {
    count?: number;
}

interface ImproveArgs {
    id?: string;
    storage?: EStorageType;
    ext?: string;
    size?: number;
    type?: ProcessingType;
}

const SHOW_POPUP_TIME = 5000;

const initAiToolsOnce = once((dispatch) =>
    isAiPhotoImproveEnabled || isAiRemoveBackgroundEnabled ? dispatch(aiToolsAttemptsInit()) : undefined
);

export const sendRadar = (storage, action: 'show' | 'click' = 'show', button_name = '', isImprove = true) =>
    sendDwhRadar({
        t: 'viewtlbar',
        action,
        name: isImprove ? 'mnz_ai_improve' : name,
        button_name,
        data: {
            storage,
        },
    });

export const useImproveImageHandlers = (args: ImproveArgs = {}) => {
    const dispatch = useDispatch();
    const item = useSelector(ViewerSelectors.getViewerItem) as CloudFile;
    const id = (args.id ? args.id : item?.id) || '';
    const ext = (args.id ? args.ext : item?.ext) || '';
    const size = (args.id ? args.size : item?.size) || 0;
    const storage = args?.storage || item?.storage;
    const isMaxSize = size && size > fileTypesForAiPhotoImproveMaxSize;
    const availableCount = useSelector(getAiImageImproveAvailableAttempts);
    const availableCountRmvBgrnd = useSelector(getAiRmvBgrndAvailableAttempts);
    const state = useSelector((state: RootState) => getImageImprovingState(state, id));
    const type = useSelector((state: RootState) => getAiProcessType(state, id));
    const isNewbackground = useSelector((state: RootState) => getIsNewbackground(state, id));
    const isPaymium = useSelector(isPaymiumSelector);
    const endLimitTime = useSelector(getAiToolsEndTime);
    const endLimitTimeRmvBgrnd = useSelector(getAiToolsRmvBgrndEndTime);
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isCorpUser = useSelector(UserSelectors.isCorpUser);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const isOverquota = useSelector(UserQuotaSelectors.isOverquota);

    const isProcessing = state === 'processing';
    const isCompare = state === 'compare';
    const isSuccess = state === 'success';
    const isDone = isSuccess || isCompare;

    const isEnabled = availableCount > 0;
    const isEnabledRmvBgrnd = aiRmvBgrndNoLimit || availableCountRmvBgrnd > 0;

    const handlePayClick = useCallback(
        (type: ProcessingType | undefined = undefined) => {
            sendRadar(storage, 'click', 'pay');
            openThreeTariffsPopup({
                dispatch,
                paySource,
                title: type === 'removeBackground' ? undefined : aiImproveTariffsTitle,
                buyTitle: type === 'removeBackground' ? undefined : aiImprovePaymentTitle,
            });
        },
        [dispatch, storage]
    );

    const handleProcessImageRequest = useCallback(
        (type: ProcessingType) => {
            if (isProcessing) {
                return;
            }

            sendRadar(storage, 'click', '', type === 'improve');

            if (isMaxSize) {
                dispatch(
                    showSnackbarAction({
                        id: 'imageImproveErrorMax',
                        type: SnackbarTypes.failure,
                        // eslint-disable-next-line no-irregular-whitespace
                        text: `Не удалось ${type === 'improve' ? 'улучшить фото' : 'удалить фон'} — ${
                            type === 'improve' ? 'оно' : 'фото'
                            // eslint-disable-next-line no-irregular-whitespace
                        } весит больше ${bytesToNDigits(fileTypesForAiPhotoImproveMaxSize, 3).value}`,
                        closable: true,
                    })
                );
                return;
            }

            if (!isEnabled && type === 'improve') {
                renderAiLimitPromo({ isPaymium, endLimitTime, onPayClick: () => handlePayClick(type) });
                return;
            }

            if (!isEnabledRmvBgrnd && type === 'removeBackground') {
                renderAiLimitPromo({ type, isPaymium, endLimitTime: endLimitTimeRmvBgrnd, onPayClick: () => handlePayClick(type) });
                return;
            }

            dispatch(improveImageRequest({ id, storage, ext, type }));
        },
        [
            isProcessing,
            storage,
            isMaxSize,
            isEnabled,
            isEnabledRmvBgrnd,
            dispatch,
            id,
            ext,
            type,
            isPaymium,
            endLimitTime,
            handlePayClick,
            endLimitTimeRmvBgrnd,
        ]
    );

    const handleImproveClick = useCallback(() => handleProcessImageRequest('improve'), [handleProcessImageRequest]);
    const handleRemoveBackgroundClick = useCallback(() => handleProcessImageRequest('removeBackground'), [handleProcessImageRequest]);

    useEffect(() => {
        initAiToolsOnce(dispatch);
    }, []);

    useEffect(() => {
        let timerId: ReturnType<typeof setTimeout> | null = null;

        if (isPaymium || isPaidUser || isBizUser || isCorpUser || isOverquota || !isSuccess) {
            return;
        }

        if (enableAiImprovePromoPopupAfterImprove) {
            const count = getDataFromStorage<PopupShowData>(promoPopupId)?.count || 0;

            if (count >= MaxShowPromoPopupCount) {
                return;
            }

            timerId = setTimeout(() => {
                renderAfterImprovePromo({
                    endLimitTime,
                    onPayClick: handlePayClick,
                    onShow: () => saveDataToStorage<PopupShowData>(promoPopupId, { count: count + 1 }),
                });
            }, SHOW_POPUP_TIME);
        } else if (enableAiRmvPromoPopupAfterRmv) {
            const count = getDataFromStorage<PopupShowData>(promoPopupIdAfterRmv)?.count || 0;

            if (count >= MaxShowPromoPopupCount) {
                return;
            }

            timerId = setTimeout(() => {
                renderAfterRmvPromo({
                    endLimitTime: endLimitTimeRmvBgrnd,
                    onPayClick: () => handlePayClick('removeBackground'),
                    onShow: () => saveDataToStorage<PopupShowData>(promoPopupIdAfterRmv, { count: count + 1 }),
                });
            }, SHOW_POPUP_TIME);
        }

        return () => {
            if (timerId) {
                clearTimeout(timerId);
                timerId = null;
            }
        };
    }, [isSuccess, isPaymium, endLimitTime, handlePayClick, isBizUser, isCorpUser, isOverquota, isPaidUser, endLimitTimeRmvBgrnd]);

    return {
        availableCount,
        availableCountRmvBgrnd,
        isDone,
        isPaymium,
        isCompare,
        isProcessing,
        endLimitTime,
        endLimitTimeRmvBgrnd,
        handleImproveClick,
        handleRemoveBackgroundClick,
        type,
        isNewbackground,
    };
};
